import { useEffect, useState } from 'react';
import { api } from '@/api';
import type { Project, ProjectWithMembers, SafeUser } from '@ultisim/shared';
import { PROJECT_COLORS } from '@ultisim/shared';

export default function AdminProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<SafeUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectWithMembers | null>(null);
  const [newProject, setNewProject] = useState({ name: '', description: '', clientName: '', color: PROJECT_COLORS[0] as string });
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectsData, usersData] = await Promise.all([
        api.get<Project[]>('/projects'),
        api.get<SafeUser[]>('/users'),
      ]);
      setProjects(projectsData);
      setUsers(usersData.filter(u => u.status === 'active'));
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const createProject = async () => {
    setError('');
    try {
      await api.post('/projects', newProject);
      setNewProject({ name: '', description: '', clientName: '', color: PROJECT_COLORS[0] });
      setIsAddingProject(false);
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create project';
      setError(message);
    }
  };

  const archiveProject = async (projectId: string) => {
    if (!confirm('Archive this project?')) return;
    try {
      await api.delete(`/projects/${projectId}`);
      loadData();
    } catch (err) {
      console.error('Failed to archive project:', err);
    }
  };

  const assignUser = async (projectId: string, userId: string) => {
    try {
      await api.post(`/projects/${projectId}/members`, { userId });
      // Reload the selected project to get updated members
      if (selectedProject?.id === projectId) {
        const updated = await api.get<ProjectWithMembers>(`/projects/${projectId}`);
        setSelectedProject(updated);
      }
      loadData();
    } catch (err) {
      console.error('Failed to assign user:', err);
    }
  };

  const selectProject = async (project: Project) => {
    if (selectedProject?.id === project.id) {
      setSelectedProject(null);
    } else {
      // Fetch full project with members
      const projectWithMembers = await api.get<ProjectWithMembers>(`/projects/${project.id}`);
      setSelectedProject(projectWithMembers);
    }
  };

  // Get available users (those not already assigned to the selected project)
  const getAvailableUsers = () => {
    if (!selectedProject?.members) return users;
    const assignedUserIds = new Set(selectedProject.members.map(m => m.userId));
    return users.filter(u => !assignedUserIds.has(u.id));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Projects</h1>
          <p className="text-dark-400 mt-1">Manage projects and team assignments</p>
        </div>
        <button onClick={() => setIsAddingProject(true)} className="btn-primary">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Project
        </button>
      </div>

      {isAddingProject && (
        <div className="card animate-slide-down">
          <h3 className="text-lg font-medium text-white mb-4">New Project</h3>
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}
          <div className="space-y-4 mb-4">
            <input
              type="text"
              placeholder="Project Name"
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              className="input"
            />
            <input
              type="text"
              placeholder="Client Name (optional)"
              value={newProject.clientName}
              onChange={(e) => setNewProject({ ...newProject, clientName: e.target.value })}
              className="input"
            />
            <textarea
              placeholder="Description (optional)"
              value={newProject.description}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              className="input"
              rows={3}
            />
            <div>
              <label className="label">Color</label>
              <div className="flex gap-2 flex-wrap">
                {PROJECT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewProject({ ...newProject, color })}
                    className={`w-8 h-8 rounded-lg transition-transform ${
                      newProject.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-dark-900 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={createProject} className="btn-primary">Create Project</button>
            <button onClick={() => setIsAddingProject(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div
            key={project.id}
            className="card border-l-4 cursor-pointer hover:bg-dark-800/50 transition-colors"
            style={{ borderLeftColor: project.color }}
            onClick={() => selectProject(project)}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium text-white">{project.name}</h3>
                {project.clientName && (
                  <p className="text-sm text-dark-400 mt-1">{project.clientName}</p>
                )}
              </div>
              <span className={`badge ${
                project.status === 'active' ? 'badge-success' :
                project.status === 'completed' ? 'badge-primary' :
                project.status === 'on_hold' ? 'badge-warning' : 'badge-gray'
              }`}>
                {project.status}
              </span>
            </div>
            {project.description && (
              <p className="text-sm text-dark-500 mt-2 line-clamp-2">{project.description}</p>
            )}

            {selectedProject?.id === project.id && (
              <div className="mt-4 pt-4 border-t border-dark-700 animate-fade-in">
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); archiveProject(project.id); }}
                    className="btn-sm btn-danger"
                  >
                    Archive
                  </button>
                </div>
                <div>
                  <label className="label">Assign Member</label>
                  {getAvailableUsers().length > 0 ? (
                    <select
                      className="input"
                      onChange={(e) => {
                        if (e.target.value) {
                          assignUser(project.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">Select user...</option>
                      {getAvailableUsers().map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.firstName} {user.lastName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-dark-400">All users are already assigned</p>
                  )}
                </div>
                {selectedProject?.members && selectedProject.members.length > 0 && (
                  <div className="mt-3">
                    <label className="label">Current Members</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.members.map((member) => (
                        <span key={member.id} className="badge badge-primary">
                          {member.user?.firstName} {member.user?.lastName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-dark-800 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No projects yet</h3>
          <p className="text-dark-400">Create your first project to get started</p>
        </div>
      )}
    </div>
  );
}
